// ---------------------------------------------------------------------------
// SAC-SMA model (Sacramento Soil Moisture Accounting) — Burnash, Ferral,
// McGuire (1973), "A Generalized Streamflow Simulation System", NWS/California
// DWR. The most complex aggregated conceptual model EvalHid implements: 6
// reservoirs, non-linear percolation dependent on the combined deficit of
// the 3 lower zones, and internal sub-iteration within each time step for
// numerical stability (NINC/DINC).
//
// Port confirmed by decompiling EvalHid's real calculation engine
// (Evalhid.dll, .NET 6, via ilspycmd — see class Sac.calculo_sac()), which
// broadly matches the NWS's original FORTRAN (sac1.f, via
// tanerumit/sacsmaR's R translation) except for two real differences:
//
// 1. The sub-iteration coefficients (DUZ/DLZP/DLZS) are literally K^y (e.g.
//    uzk^y), NOT the "textbook" formula 1-(1-K)^y that both the original
//    FORTRAN and the R reference use. For y=1 (a single sub-step, the most
//    common case) both formulas coincide by pure algebraic identity
//    (1-(1-K)=K), but they diverge for y<1 (several sub-steps).
// 2. The number of sub-steps (NINC) is calculated with `Convert.ToInt32`,
//    which in .NET rounds to the nearest integer rather than truncating —
//    `Math.round` is used here as the equivalent (the difference from
//    .NET's exact "half-to-even" rounding would only matter on an exact
//    half-way tie, an edge case with real precipitation data).
//
// EvalHid calls the parameter the NWS literature calls RSERV "Saved" —
// same role, same value, just a different name. Variable names are kept
// the same as the source (et1, twx, perc, uztwc...) on purpose, to be able
// to cross-check directly against the source code line by line if needed.
//
// With a real aquifer assigned, `contribution` excludes the base flow
// component (`bfcc`) — confirmed in the real source (2026-08, see
// CLAUDE.md's engine-source audit): `Sac_sma.cs` assumes it flows to the
// aquifer's own aggregate balance instead (same pattern as the other 5
// models, see aggregation.ts).
//
// Pumping (`bomb`, mm/step): unlike the other 5 models (which subtract it
// once per outer time step), SAC subtracts an even share of it
// (`bomb/ninc`) from the primary base flow reservoir (`lzfpc`) at EVERY
// sub-step of the internal NINC loop, right alongside that sub-step's own
// base flow withdrawal (`Sac_sma.cs:208`, added "Mayo 2024"):
// `lzfpc = lzfpc - bf_p - (bomb/ninc)` — added later than the rest of this
// file's own pumping ports, straight into the existing `lzfpc -= bf_p`
// clamp (the `<= 0.0001` floor right after already covers a pumping
// withdrawal driving it negative, same as bf_p alone could before).
// Resolved from the sub-basin's own `Ent_Bomb.csv` column and converted to
// mm/step before reaching here — see temez.ts's own header for the full
// resolution/conversion chain, shared by all 6 models. `null` only when
// the sub-basin has no pumping column of its own, or "Modelar bombeos" is
// unchecked.
// ---------------------------------------------------------------------------

export interface SacParameters {
  /** UZTWM (mm): upper zone tension water maximum storage. */
  uztwm: number;
  /** UZFWM (mm): upper zone free water maximum storage. */
  uzfwm: number;
  /** LZTWM (mm): lower zone tension water maximum storage. */
  lztwm: number;
  /** LZFPM (mm): lower zone free water primary (slow-draining) maximum storage. */
  lzfpm: number;
  /** LZFSM (mm): lower zone free water supplemental (fast-draining) maximum storage. */
  lzfsm: number;
  /** 1/step. */
  uzk: number;
  /** 1/step. */
  lzpk: number;
  /** 1/step. */
  lzsk: number;
  /** ZPERC: multiplier setting the maximum percolation rate (dry-soil demand), relative to the minimum (saturated-soil) rate. */
  zperc: number;
  /** REXP: exponent shaping the percolation curve between the ZPERC-scaled maximum and the minimum rate. */
  rexp: number;
  /** PFREE: fraction of percolated water routed straight to the lower zone's free water (bypassing tension water). */
  pfree: number;
  /** PCTIM: permanent impervious fraction of the basin (always-impervious, independent of soil saturation). */
  pctim: number;
  /** ADIMP: additional impervious fraction the basin can reach on top of PCTIM as the upper zone saturates. */
  adimp: number;
  /** RIVA: riparian vegetation fraction — area that evapotranspires directly from the lower zone's free water, unlike the rest of the basin. */
  riva: number;
  /** SIDE: ratio of deep (non-channel) recharge to base flow — the fraction of lower-zone drainage that leaves as base flow gets divided by (1+SIDE). */
  side: number;
  /** RSERV in the NWS literature: fraction of lower-zone free water not transferable. */
  saved: number;
}

interface SacState {
  /** UZTWC (mm): upper zone tension water content. */
  uztwc: number;
  /** UZFWC (mm): upper zone free water content. */
  uzfwc: number;
  /** LZTWC (mm): lower zone tension water content. */
  lztwc: number;
  /** LZFSC (mm): lower zone free water supplemental (fast-draining) content. */
  lzfsc: number;
  /** LZFPC (mm): lower zone free water primary (slow-draining) content. */
  lzfpc: number;
  /** ADIMC in the NWS literature: additional impervious area storage. */
  adim: number;
}

interface SacDetail {
  [key: string]: Float64Array;
  uztwc: Float64Array;
  uzfwc: Float64Array;
  lztwc: Float64Array;
  lzfsc: Float64Array;
  lzfpc: Float64Array;
  adim: Float64Array;
  /**
   * Etr (mm): total actual evapotranspiration for the step — `micu.etr` in
   * the real source (`(et1+et2+et3)*parea + et4_r`, confirmed 2026-08
   * against `Sac_sma.cs`, see CLAUDE.md's engine-source audit; an earlier
   * verification against a decompiled Evalhid.dll couldn't pin this down
   * precisely and used an approximation instead — this now matches exactly).
   */
  etr: Float64Array;
  /**
   * Surface runoff (mm) — `micu.esc_sup` in the real source
   * (`Sac_sma.cs:386-409`), for `aggregation.ts`'s SUP_SUBT_KEYS.
   */
  surfaceRunoff: Float64Array;
  /**
   * Base flow (mm) — `micu.esc_subt` in the real source. Exposed
   * regardless of `hasAquifer` (unlike `contribution`, which excludes it
   * when true) — with a real aquifer assigned, this is the actual
   * infiltration this sub-basin feeds into that aquifer's own balance (see
   * `aggregation.ts`'s `aggregateContributionsByAquifer`), not just
   * informational.
   */
  baseFlow: Float64Array;
}

interface SacResult {
  /** Total contribution (surface + base) per time step, mm. */
  contribution: Float64Array;
  /** Step-by-step internal detail series (see Sal_parciales.csv in the real .exe). */
  detail: SacDetail;
}

const ZERO_THRESHOLD = 0.00001;

/**
 * Simulates the SAC-SMA model for a single sub-basin. `precipitation` and
 * `etp` must already come adjusted (precipAdjustment/etpAdjustment applied) and aligned step
 * by step.
 */
export function calculateSac(
  precipitation: Float64Array,
  etp: Float64Array,
  parameters: SacParameters,
  initialState: SacState,
  hasAquifer: boolean,
  pumping: Float64Array | null
): SacResult {
  const {
    uztwm,
    uzfwm,
    lztwm,
    lzfpm,
    lzfsm,
    uzk,
    lzpk,
    lzsk,
    zperc,
    rexp,
    pfree,
    pctim,
    adimp,
    riva,
    side,
    saved: rserv,
  } = parameters;
  const n = precipitation.length;
  const contribution = new Float64Array(n);
  const detail: SacDetail = {
    uztwc: new Float64Array(n),
    uzfwc: new Float64Array(n),
    lztwc: new Float64Array(n),
    lzfsc: new Float64Array(n),
    lzfpc: new Float64Array(n),
    adim: new Float64Array(n),
    etr: new Float64Array(n),
    surfaceRunoff: new Float64Array(n),
    baseFlow: new Float64Array(n),
  };

  let uztwc = initialState.uztwc;
  let uzfwc = initialState.uzfwc;
  let lztwc = initialState.lztwc;
  let lzfsc = initialState.lzfsc;
  let lzfpc = initialState.lzfpc;
  let adimc = initialState.adim;

  const parea = 1 - adimp - pctim;

  for (let t = 0; t < n; t++) {
    const pr = Number.isFinite(precipitation[t]) ? precipitation[t] : 0;
    const edmnd = Number.isFinite(etp[t]) ? etp[t] : 0;
    const bomb = pumping && Number.isFinite(pumping[t]) ? pumping[t] : 0;

    // ET1: upper zone tension water.
    let et1 = (edmnd * uztwc) / uztwm;
    let red = edmnd - et1;
    uztwc -= et1;

    let et2 = 0;
    if (uztwc <= 0) {
      et1 += uztwc;
      uztwc = 0;
      red = edmnd - et1;

      if (uzfwc < red) {
        et2 = uzfwc;
        uzfwc = 0;
        red -= et2;
        if (uztwc < ZERO_THRESHOLD) uztwc = 0;
        if (uzfwc < ZERO_THRESHOLD) uzfwc = 0;
      } else {
        et2 = red;
        uzfwc -= et2;
        red = 0;
      }
    } else {
      // If free water is proportionally fuller than tension water, transfer between them.
      if (uztwc / uztwm < uzfwc / uzfwm) {
        const uzrat = (uztwc + uzfwc) / (uztwm + uzfwm);
        uztwc = uztwm * uzrat;
        uzfwc = uzfwm * uzrat;
      }
      if (uztwc < ZERO_THRESHOLD) uztwc = 0;
      if (uzfwc < ZERO_THRESHOLD) uzfwc = 0;
    }

    // ET3: lower zone tension water.
    let et3 = (red * lztwc) / (uztwm + lztwm);
    lztwc -= et3;
    if (lztwc < 0) {
      et3 += lztwc;
      lztwc = 0;
    }

    // Resupply of lower zone free water towards lower zone tension water.
    const savedWater = rserv * (lzfpm + lzfsm);
    const ratlzt = lztwc / lztwm;
    const ratlz = (lztwc + lzfpc + lzfsc - savedWater) / (lztwm + lzfpm + lzfsm - savedWater);
    if (ratlzt < ratlz) {
      const del = (ratlz - ratlzt) * lztwm;
      lztwc += del;
      lzfsc -= del;
      if (lzfsc < 0) {
        lzfpc += lzfsc;
        lzfsc = 0;
      }
    }
    if (lztwc < ZERO_THRESHOLD) lztwc = 0;

    // ET5: additional impervious area (ADIMP).
    let et5 = et1 + ((red + et2) * (adimc - et1 - uztwc)) / (uztwm + lztwm);
    adimc -= et5;
    if (adimc < 0) {
      et5 += adimc;
      adimc = 0;
    }
    et5 *= adimp;

    // Available moisture in excess of the upper zone tension capacity.
    let twx = pr + uztwc - uztwm;
    if (twx < 0) {
      uztwc += pr;
      twx = 0;
    } else {
      uztwc = uztwm;
    }
    adimc += pr - twx;

    const roimp = pr * pctim;

    let sbf = 0;
    let ssur = 0;
    let sif = 0;
    let sdro = 0;

    // Internal step sub-iteration, for percolation numerical stability.
    const ninc = Math.max(1, Math.round(1.0 + 0.2 * (uzfwc + twx)));
    const dinc = 1.0 / ninc;
    const pinc = twx / ninc;

    const duz = uzk ** dinc;
    const dlzp = lzpk ** dinc;
    const dlzs = lzsk ** dinc;

    for (let k = 0; k < ninc; k++) {
      let adsur = 0;

      let ratio = (adimc - uztwc) / lztwm;
      if (ratio < 0) ratio = 0;
      let addro = pinc * ratio ** 2;

      // Base flow, primary reservoir.
      let bfP = lzfpc * dlzp;
      lzfpc = lzfpc - bfP - bomb / ninc;
      if (lzfpc <= 0.0001) {
        bfP += lzfpc;
        lzfpc = 0;
      }
      sbf += bfP;

      // Base flow, supplemental reservoir.
      let bfS = lzfsc * dlzs;
      lzfsc -= bfS;
      if (lzfsc <= 0.0001) {
        bfS += lzfsc;
        lzfsc = 0;
      }
      sbf += bfS;

      if (pinc + uzfwc <= 0.01) {
        uzfwc += pinc;
      } else {
        // Maximum percolation rate based on the combined deficit of the 3 lower zones.
        const percm = lzfpm * dlzp + lzfsm * dlzs;
        let perc = (percm * uzfwc) / uzfwm;
        let defr = 1.0 - (lztwc + lzfpc + lzfsc) / (lztwm + lzfpm + lzfsm);
        if (defr < 0) defr = 0;
        perc *= 1.0 + zperc * defr ** rexp;

        if (perc >= uzfwc) perc = uzfwc;
        uzfwc -= perc;

        const check = lztwc + lzfpc + lzfsc + perc - lztwm - lzfpm - lzfsm;
        if (check > 0) {
          perc -= check;
          uzfwc += check;
        }

        // Interflow (with the remaining free water, before adding PINC).
        const del = uzfwc * duz;
        sif += del;
        uzfwc -= del;

        // Percolation split: tension water first, except for the PFREE fraction.
        const perct = perc * (1.0 - pfree);
        let percf: number;
        if (perct + lztwc <= lztwm) {
          lztwc += perct;
          percf = 0;
        } else {
          percf = lztwc + perct - lztwm;
          lztwc = lztwm;
        }
        percf += perc * pfree;

        if (percf !== 0) {
          const hpl = lzfpm / (lzfpm + lzfsm);
          const ratlp = lzfpc / lzfpm;
          const ratls = lzfsc / lzfsm;
          let fracp = (hpl * 2 * (1 - ratlp)) / (2 - ratlp - ratls);
          if (fracp > 1.0) fracp = 1.0;

          const percp = percf * fracp;
          let percs = percf - percp;
          lzfsc += percs;
          if (lzfsc > lzfsm) {
            percs = percs - lzfsc + lzfsm;
            lzfsc = lzfsm;
          }

          lzfpc += percf - percs;
          if (lzfpc >= lzfpm) {
            const excess = lzfpc - lzfpm;
            lztwc += excess;
            lzfpc = lzfpm;
          }
        }

        // PINC split between upper zone free water and surface runoff.
        if (pinc !== 0) {
          if (pinc + uzfwc <= uzfwm) {
            uzfwc += pinc;
          } else {
            const sur = pinc + uzfwc - uzfwm;
            uzfwc = uzfwm;
            ssur += sur * parea;
            adsur = sur * (1.0 - addro / pinc);
            ssur += adsur * adimp;
          }
        }
      }

      adimc += pinc - addro - adsur;
      if (adimc > uztwm + lztwm) {
        addro = addro + adimc - (uztwm + lztwm);
        adimc = uztwm + lztwm;
      }
      sdro += addro * adimp;

      if (adimc < ZERO_THRESHOLD) adimc = 0;
    }

    // `eused` here is RAW (not yet ×parea) — matches the real source's own
    // `eused` right up until its later `eused = eused * parea` reassignment
    // (see `eusedArea` below); `et4` also uses this raw value, same as the
    // real source.
    const eused = et1 + et2 + et3;
    sif *= parea;

    const tbf = sbf * parea;
    const bfcc = tbf / (1 + side);

    const surfBruto = roimp + sdro + ssur + sif;
    const et4 = (edmnd - eused) * riva;

    if (adimc < uztwc) adimc = uztwc;

    // Total flow is flowBase-et4_r, bounded to ≥0 — flowBase excludes
    // `bfcc` (base flow) when the sub-basin has a real aquifer assigned
    // (confirmed in the real source, 2026-08, see CLAUDE.md's engine-source
    // audit: `Sac_sma.cs` assumes it flows to the aquifer's own aggregate
    // balance instead, see aggregation.ts). The real source computes the
    // ACTUAL (bounded) riparian ET applied, `et4_r`, via its own branching
    // logic (capped first by `surf` alone, then — without a real aquifer,
    // only when et4 exceeds `surf` — allowed to dip into `base_f` too) and
    // exposes it separately as `micu.etr`; algebraically that bounded value
    // is always exactly `flowBase - contribution` (both branches of the
    // real source's own logic reduce to this), so it's derived here instead
    // of replicated branch-by-branch.
    const flowBase = hasAquifer ? surfBruto : surfBruto + bfcc;
    contribution[t] = Math.max(0, flowBase - et4);
    const et4Applied = flowBase - contribution[t];

    // Surface/underground split — Sac_sma.cs:386-409 (`micu.esc_sup`/
    // `micu.esc_subt`), needed for aggregation.ts's SUP_SUBT_KEYS: the
    // outlet's own informational breakdown, and — when a real aquifer is
    // assigned — the actual infiltration this sub-basin feeds into that
    // aquifer's own balance. With a real aquifer, ET4 is capped by `surf`
    // alone even if it would otherwise exceed it (never dips into
    // `base_f`) — `surfaceRunoff` is `max(0, surf-et4)` in BOTH branches
    // (confirmed algebraically against the real source's own two
    // branches), but `baseFlow` differs: always the FULL, untouched `bfcc`
    // with a real aquifer; reduced by whatever ET4 "overflow" (et4
    // exceeding surf) eats into it otherwise.
    const surfaceRunoff = Math.max(0, surfBruto - et4);
    const baseFlow = hasAquifer ? bfcc : Math.max(0, bfcc - Math.max(0, et4 - surfBruto));

    // Matches the real source's own `eused = eused * parea`, applied AFTER
    // et4 (which uses the raw eused above).
    const eusedArea = eused * parea;

    detail.uztwc[t] = uztwc;
    detail.uzfwc[t] = uzfwc;
    detail.lztwc[t] = lztwc;
    detail.lzfsc[t] = lzfsc;
    detail.lzfpc[t] = lzfpc;
    detail.adim[t] = adimc;
    detail.surfaceRunoff[t] = surfaceRunoff;
    detail.baseFlow[t] = baseFlow;
    detail.etr[t] = eusedArea + et4Applied;
  }

  return { contribution, detail };
}
